"use client"

import type React from "react"
import { useState } from "react"
import { Box, Chip, useMediaQuery, useTheme, Container, Button } from "@mui/material"
import { IconChevronDown, IconChevronUp } from "@tabler/icons-react"
import type { Category } from "../types"

interface CategoryFilterProps {
    categories: Category[]
    selectedCategory: string
    onCategoryChange: (category: string) => void
    showInStockOnly: boolean
    onInStockChange: (checked: boolean) => void
}

const CategoryFilter: React.FC<CategoryFilterProps> = ({
    categories,
    selectedCategory,
    onCategoryChange,
    showInStockOnly,
    onInStockChange,
}) => {
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down("md"))
    const [expanded, setExpanded] = useState(false)

    // Calculate how many categories to show in collapsed state
    const visibleCategoriesCount = isMobile ? 3 : 6
    const hasMoreCategories = categories.length > visibleCategoriesCount

    const toggleExpanded = () => {
        setExpanded(!expanded)
    }

    // Determine which categories to show based on expanded state
    const visibleCategories = expanded ? categories : categories.slice(0, visibleCategoriesCount)

    return (
        <Box
            sx={{
                backgroundColor: "#e9ede5",
                py: isMobile ? 1 : 1.5,
                boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                position: "sticky",
                top: { xs: "64px", md: "64px" }, // Adjusted to be below header
                zIndex: 1100,
                width: "100%",
                overflowX: "hidden",
            }}
        >
            <Container
                maxWidth="lg"
                sx={{
                    px: { xs: 1, sm: 2 },
                }}
            >
                <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "nowrap",
                            gap: isMobile ? 0.5 : 1,
                            alignItems: "center",
                            justifyContent: { xs: "flex-start", md: "flex-start" },
                            overflowX: "auto",
                            pb: 0.5,
                            "&::-webkit-scrollbar": {
                                display: "none",
                            },
                            scrollbarWidth: "none",
                            msOverflowStyle: "none",
                        }}
                    >
                        <Chip
                            label="Solo listos para entrega"
                            clickable
                            color={showInStockOnly ? "primary" : "default"}
                            onClick={() => onInStockChange(!showInStockOnly)}
                            variant={showInStockOnly ? "filled" : "outlined"}
                            sx={{
                                fontWeight: showInStockOnly ? 600 : 400,
                                ml: { xs: 0, md: "auto" },
                                "&:hover": {
                                    backgroundColor: showInStockOnly ? "#0d6336" : "#e0e0e0",
                                },
                                fontSize: isMobile ? "0.75rem" : "0.875rem",
                                height: isMobile ? "28px" : "32px",
                                flexShrink: 0,
                            }}
                        />

                        <Chip
                            label="Todos"
                            clickable
                            color={selectedCategory === "all" ? "primary" : "default"}
                            onClick={() => onCategoryChange("all")}
                            sx={{
                                fontWeight: selectedCategory === "all" ? 600 : 400,
                                "&:hover": {
                                    backgroundColor: selectedCategory === "all" ? "#0d6336" : "#e0e0e0",
                                },
                                fontSize: isMobile ? "0.75rem" : "0.875rem",
                                height: isMobile ? "28px" : "32px",
                            }}
                        />
                    </Box>

                    {/* Categories section with collapse */}
                    <Box sx={{ mt: 1 }}>
                        <Box
                            sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: isMobile ? 0.5 : 1,
                                alignItems: "center",
                                justifyContent: "flex-start",
                            }}
                        >
                            {visibleCategories.map((category) => (
                                <Chip
                                    key={category.id}
                                    label={category.name}
                                    clickable
                                    color={selectedCategory === category.name ? "primary" : "default"}
                                    onClick={() => onCategoryChange(category.name)}
                                    sx={{
                                        fontWeight: selectedCategory === category.name ? 600 : 400,
                                        "&:hover": {
                                            backgroundColor: selectedCategory === category.name ? "#0d6336" : "#e0e0e0",
                                        },
                                        fontSize: isMobile ? "0.75rem" : "0.875rem",
                                        height: isMobile ? "28px" : "32px",
                                        mb: 0.5,
                                    }}
                                />
                            ))}

                            {/* Show more/less button only if there are more categories */}
                            {hasMoreCategories && (
                                <Button
                                    variant="text"
                                    size="small"
                                    onClick={toggleExpanded}
                                    startIcon={expanded ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
                                    sx={{
                                        color: "#0d6336",
                                        fontSize: isMobile ? "0.75rem" : "0.875rem",
                                        minWidth: 0,
                                        p: "4px 8px",
                                        mb: 0.5,
                                    }}
                                >
                                    {expanded ? "Menos" : "Más"}
                                </Button>
                            )}
                        </Box>
                    </Box>
                </Box>
            </Container>
        </Box>
    )
}

export default CategoryFilter

